import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  createAccount,
  deleteAccount,
  fetchAccounts,
  fetchCompanies,
  patchAccount,
  type Account,
  type Company,
} from "../api";
import { formatIban, formatMoney, subtypeLabel } from "../lib/grouping";

const SUBTYPES = ["checking", "savings", "line-of-credit"] as const;

export function Accounts() {
  const [items, setItems] = useState<Account[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    iban: "",
    bankCode: "",
    bankName: "",
    companyId: "",
    alias: "",
    currency: "EUR",
    subtype: "checking",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [a, c] = await Promise.all([fetchAccounts(), fetchCompanies()]);
      setItems(a.items);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Orden: primero por empresa (alias o nombre, en español), luego por IBAN.
  const sortedItems = useMemo(() => {
    if (!items) return null;
    const companyById = new Map(companies.map((c) => [c.id, c]));
    const labelOf = (companyId: string) => {
      const c = companyById.get(companyId);
      return (c?.alias || c?.name || companyId || "").toLowerCase();
    };
    return [...items].sort((a, b) => {
      const la = labelOf(a.companyId);
      const lb = labelOf(b.companyId);
      if (la !== lb) return la.localeCompare(lb, "es");
      return a.iban.localeCompare(b.iban);
    });
  }, [items, companies]);

  useEffect(() => {
    void load();
  }, []);

  const save = async () => {
    if (!draft.iban.trim() || !draft.bankCode.trim() || !draft.companyId) {
      alert("Indica IBAN, código de banco y empresa.");
      return;
    }
    setBusy(true);
    try {
      await createAccount({
        iban: draft.iban,
        bankCode: draft.bankCode,
        bankName: draft.bankName.trim() || undefined,
        companyId: draft.companyId,
        alias: draft.alias.trim() || undefined,
        currency: draft.currency,
        subtype: draft.subtype,
        notes: draft.notes.trim() || undefined,
      });
      setDraft({
        iban: "",
        bankCode: "",
        bankName: "",
        companyId: "",
        alias: "",
        currency: "EUR",
        subtype: "checking",
        notes: "",
      });
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Cuentas bancarias</h2>
          <p className="meta">
            Da de alta cada cuenta con su IBAN, banco y empresa propietaria.
            Los balances se actualizarán solos con cada refresco del banco.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={() => setCreating((x) => !x)} disabled={companies.length === 0}>
            {creating ? "✕ Cancelar" : "+ Nueva cuenta"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {companies.length === 0 && (
        <p className="warning">
          No hay empresas. Primero crea al menos una empresa en la pestaña
          "Empresas" para poder dar de alta cuentas.
        </p>
      )}

      {creating && companies.length > 0 && (
        <div className="form-row" style={{ flexWrap: "wrap" }}>
          <input placeholder="IBAN" value={draft.iban} onChange={(e) => setDraft({ ...draft, iban: e.target.value })} style={{ minWidth: "16rem" }} />
          <input placeholder="bankCode (ej. santander_emp)" value={draft.bankCode} onChange={(e) => setDraft({ ...draft, bankCode: e.target.value })} />
          <input placeholder="Banco (nombre)" value={draft.bankName} onChange={(e) => setDraft({ ...draft, bankName: e.target.value })} />
          <select value={draft.companyId} onChange={(e) => setDraft({ ...draft, companyId: e.target.value })}>
            <option value="">— Empresa —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.alias || c.name} ({c.id})
              </option>
            ))}
          </select>
          <input placeholder="Alias" value={draft.alias} onChange={(e) => setDraft({ ...draft, alias: e.target.value })} />
          <input placeholder="EUR" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} style={{ width: "5rem" }} />
          <select value={draft.subtype} onChange={(e) => setDraft({ ...draft, subtype: e.target.value })}>
            {SUBTYPES.map((s) => <option key={s} value={s}>{subtypeLabel(s)}</option>)}
          </select>
          <button type="button" onClick={save} disabled={busy}>
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}
      {items && items.length === 0 && <p className="meta">No hay cuentas dadas de alta.</p>}
      {items && items.length > 0 && (
        <table className="data accounts-list">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>IBAN</th>
              <th>Alias</th>
              <th>Banco</th>
              <th>Tipo</th>
              <th className="num">Disponible</th>
              <th>Último sync</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortedItems!.map((a) => (
              <AccountRow
                key={a.iban}
                account={a}
                companies={companies}
                onUpdated={load}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AccountRow({
  account,
  companies,
  onUpdated,
}: {
  account: Account;
  companies: Company[];
  onUpdated: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [alias, setAlias] = useState(account.alias);
  const [companyId, setCompanyId] = useState(account.companyId);
  const [bankCode, setBankCode] = useState(account.bankCode);
  const [bankName, setBankName] = useState(account.bankName);
  const [isCreditLine, setIsCreditLine] = useState(account.isCreditLine);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await patchAccount(account.iban, { alias, companyId, bankCode, bankName, isCreditLine });
      setEditing(false);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!confirm(`¿Borrar la cuenta ${account.iban}? Los payloads recibidos seguirán guardados.`)) return;
    setBusy(true);
    try {
      await deleteAccount(account.iban);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const companyLabel = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? (c.alias || c.name) : id || "—";
  };

  // Botones de la columna acciones — todos con las mismas dimensiones para
  // que la fila se vea compacta y ordenada. Padding uniforme, altura mínima
  // igual y sin wrap del texto.
  const actionBtnStyle: React.CSSProperties = {
    padding: "0.2rem 0.55rem",
    minWidth: "2.2rem",
    height: "1.8rem",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.85em",
    whiteSpace: "nowrap",
  };

  return (
    <tr style={{ ...(account.active ? {} : { opacity: 0.5 }), whiteSpace: "nowrap" }}>
      <td>
        {editing ? (
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.alias || c.name} ({c.id})
              </option>
            ))}
          </select>
        ) : (
          companyLabel(account.companyId)
        )}
      </td>
      <td><code>{formatIban(account.iban)}</code></td>
      <td>
        {editing
          ? <input value={alias} onChange={(e) => setAlias(e.target.value)} style={{ width: "8rem" }} />
          : (account.alias || <span className="meta">—</span>)}
      </td>
      <td>
        {editing ? (
          <>
            <input value={bankCode} onChange={(e) => setBankCode(e.target.value)} style={{ width: "7rem" }} />
            <input value={bankName} onChange={(e) => setBankName(e.target.value)} style={{ width: "7rem" }} />
          </>
        ) : (
          <>
            <code>{account.bankCode}</code>
            {account.bankName && <span className="meta"> · {account.bankName}</span>}
          </>
        )}
      </td>
      <td>
        {editing ? (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.85em" }}>
            <input
              type="checkbox"
              checked={isCreditLine}
              onChange={(e) => setIsCreditLine(e.target.checked)}
            />
            Póliza
          </label>
        ) : (
          <>
            {subtypeLabel(account.subtype)}
            {account.isCreditLine && (
              <span
                className="badge warn"
                style={{ marginLeft: 4, fontSize: "0.75em" }}
                title="Póliza de crédito: el saldo relevante es el DISPUESTO (current), no el disponible."
              >
                Póliza
              </span>
            )}
          </>
        )}
      </td>
      <td className="num">
        {account.lastBalanceAvailable !== null
          ? formatMoney(account.lastBalanceAvailable, account.currency)
          : <span className="meta">—</span>}
      </td>
      <td>{account.lastSyncedAt ? new Date(account.lastSyncedAt).toLocaleString() : <span className="meta">nunca</span>}</td>
      <td className="actions" style={{ whiteSpace: "nowrap" }}>
        {editing ? (
          <>
            <button type="button" onClick={save} disabled={busy} style={actionBtnStyle}>OK</button>
            <button type="button" onClick={() => setEditing(false)} disabled={busy} style={actionBtnStyle}>✕</button>
          </>
        ) : (
          <>
            <Link to={`/cuentas/${encodeURIComponent(account.iban)}/movimientos`}>
              <button type="button" style={actionBtnStyle} title="Ver movimientos bancarios">Mov.</button>
            </Link>
            <Link to={`/cuentas/${encodeURIComponent(account.iban)}/apuntes-sap`}>
              <button type="button" style={actionBtnStyle} title="Ver apuntes SAP">SAP</button>
            </Link>
            <button type="button" onClick={() => setEditing(true)} style={actionBtnStyle} title="Editar">✏️</button>
            <button type="button" className="danger" onClick={onDelete} disabled={busy} style={actionBtnStyle} title="Borrar">🗑️</button>
          </>
        )}
      </td>
    </tr>
  );
}
